import { parseEventLogs } from "viem";
import { protocolErrorAbi } from "./errors.js";
import { tapeoutProjectCoreAbi } from "./tapeout.js";
const minerIdInput = [{ name: "minerId", type: "uint256" }];
const uintOutput = [{ name: "", type: "uint256" }];
export const rewardProjectCoreAbi = [
    ...tapeoutProjectCoreAbi,
    ...[
        "lastRewardTime",
        "accVerifiedPerWeightScaled",
        "accUnverifiedPerWeightScaled",
        "verifiedTotalWeight",
        "unverifiedTotalWeight",
        "verifiedDivisionRemainder",
        "unverifiedDivisionRemainder",
        "totalUnlocked",
        "totalClaimed",
        "totalForgone",
        "totalFuelBurned",
        "expiryBacklogCountHint",
    ].map((name) => ({
        type: "function",
        name,
        stateMutability: "view",
        inputs: [],
        outputs: uintOutput,
    })),
    ...[
        "minerRewardPool",
        "minerFuelMultiplier",
        "minerFuelExpiresAt",
        "minerRewardDebtScaled",
        "minerAccruedScaled",
        "pendingReward",
    ].map((name) => ({
        type: "function",
        name,
        stateMutability: "view",
        inputs: minerIdInput,
        outputs: uintOutput,
    })),
    {
        type: "function",
        name: "nextFuelExpiry",
        stateMutability: "view",
        inputs: [],
        outputs: [
            { name: "expiry", type: "uint64" },
            { name: "extraWeightDelta", type: "uint256" },
        ],
    },
    {
        type: "function",
        name: "rewardSettlementStatus",
        stateMutability: "view",
        inputs: [],
        outputs: [
            { name: "claimableThrough", type: "uint64" },
            { name: "caughtUp", type: "bool" },
            { name: "nextExpiry", type: "uint64" },
        ],
    },
    {
        type: "function",
        name: "previewFuelPrice",
        stateMutability: "view",
        inputs: [
            ...minerIdInput,
            { name: "targetMultiplier", type: "uint8" },
            { name: "periods", type: "uint8" },
        ],
        outputs: [
            { name: "burnAmount", type: "uint256" },
            { name: "resultingExpiry", type: "uint64" },
        ],
    },
    {
        type: "function",
        name: "claim",
        stateMutability: "nonpayable",
        inputs: minerIdInput,
        outputs: [{ name: "amount", type: "uint256" }],
    },
    {
        type: "function",
        name: "claimMany",
        stateMutability: "nonpayable",
        inputs: [{ name: "minerIds", type: "uint256[]" }],
        outputs: [{ name: "amount", type: "uint256" }],
    },
    {
        type: "function",
        name: "buyFuel",
        stateMutability: "payable",
        inputs: [
            ...minerIdInput,
            { name: "targetMultiplier", type: "uint8" },
            { name: "periods", type: "uint8" },
            { name: "maxBurnAmount", type: "uint256" },
            { name: "deadline", type: "uint256" },
        ],
        outputs: [
            { name: "burnedAmount", type: "uint256" },
            { name: "expiresAt", type: "uint64" },
        ],
    },
    {
        type: "function",
        name: "processExpiries",
        stateMutability: "nonpayable",
        inputs: [{ name: "maxItems", type: "uint8" }],
        outputs: [
            { name: "processed", type: "uint8" },
            { name: "settledThrough", type: "uint64" },
        ],
    },
];
export const rewardProjectTokenAbi = [
    ...protocolErrorAbi,
    ...["balanceOf"].map((name) => ({
        type: "function",
        name,
        stateMutability: "view",
        inputs: [{ name: "account", type: "address" }],
        outputs: uintOutput,
    })),
    {
        type: "function",
        name: "allowance",
        stateMutability: "view",
        inputs: [
            { name: "owner", type: "address" },
            { name: "spender", type: "address" },
        ],
        outputs: uintOutput,
    },
    {
        type: "function",
        name: "approve",
        stateMutability: "nonpayable",
        inputs: [
            { name: "spender", type: "address" },
            { name: "amount", type: "uint256" },
        ],
        outputs: [{ name: "", type: "bool" }],
    },
];
export const rewardMinerNftAbi = [
    ...protocolErrorAbi,
    ...["ownerOf", "getApproved"].map((name) => ({
        type: "function",
        name,
        stateMutability: "view",
        inputs: [{ name: "tokenId", type: "uint256" }],
        outputs: [{ name: "", type: "address" }],
    })),
    {
        type: "function",
        name: "isApprovedForAll",
        stateMutability: "view",
        inputs: [
            { name: "owner", type: "address" },
            { name: "operator", type: "address" },
        ],
        outputs: [{ name: "", type: "bool" }],
    },
];
export const rewardEventAbi = [
    {
        type: "event",
        name: "UnverifiedEmissionCapped",
        inputs: [
            { name: "project", type: "address", indexed: true },
            { name: "fromTime", type: "uint64", indexed: false },
            { name: "toTime", type: "uint64", indexed: false },
            { name: "excessMovedFromUnverified", type: "uint256", indexed: false },
            { name: "movedToVerifiedPool", type: "bool", indexed: false },
        ],
    },
    {
        type: "event",
        name: "RewardLedgerUpdated",
        inputs: [
            { name: "project", type: "address", indexed: true },
            { name: "fromTime", type: "uint64", indexed: false },
            { name: "toTime", type: "uint64", indexed: false },
            { name: "verifiedEmission", type: "uint256", indexed: false },
            { name: "unverifiedEmission", type: "uint256", indexed: false },
            { name: "verifiedTotalWeight", type: "uint256", indexed: false },
            { name: "unverifiedTotalWeight", type: "uint256", indexed: false },
        ],
    },
    {
        type: "event",
        name: "RewardCheckpointed",
        inputs: [
            { name: "project", type: "address", indexed: true },
            { name: "minerId", type: "uint256", indexed: true },
            { name: "owner", type: "address", indexed: true },
            { name: "accruedAfter", type: "uint256", indexed: false },
            { name: "status", type: "uint8", indexed: false },
            { name: "effectiveWeight", type: "uint8", indexed: false },
        ],
    },
    {
        type: "event",
        name: "RewardClaimed",
        inputs: [
            { name: "project", type: "address", indexed: true },
            { name: "minerId", type: "uint256", indexed: true },
            { name: "owner", type: "address", indexed: true },
            { name: "amount", type: "uint256", indexed: false },
            { name: "totalClaimedAfter", type: "uint256", indexed: false },
        ],
    },
    {
        type: "event",
        name: "TokenEmissionForgone",
        inputs: [
            { name: "project", type: "address", indexed: true },
            { name: "pool", type: "uint8", indexed: true },
            { name: "fromTime", type: "uint64", indexed: false },
            { name: "toTime", type: "uint64", indexed: false },
            { name: "amount", type: "uint256", indexed: false },
            { name: "totalForgoneAfter", type: "uint256", indexed: false },
        ],
    },
    {
        type: "event",
        name: "FuelPurchased",
        inputs: [
            { name: "project", type: "address", indexed: true },
            { name: "minerId", type: "uint256", indexed: true },
            { name: "payer", type: "address", indexed: true },
            { name: "previousMultiplier", type: "uint8", indexed: false },
            { name: "newMultiplier", type: "uint8", indexed: false },
            { name: "periods", type: "uint8", indexed: false },
            { name: "burnedAmount", type: "uint256", indexed: false },
            { name: "expiresAt", type: "uint64", indexed: false },
        ],
    },
    {
        type: "event",
        name: "FuelExpiryScheduled",
        inputs: [
            { name: "project", type: "address", indexed: true },
            { name: "expiresAt", type: "uint64", indexed: true },
            { name: "addedExtraWeight", type: "uint256", indexed: false },
            { name: "totalDeltaAtExpiry", type: "uint256", indexed: false },
        ],
    },
    {
        type: "event",
        name: "FuelExpiriesProcessed",
        inputs: [
            { name: "project", type: "address", indexed: true },
            { name: "fromExpiry", type: "uint64", indexed: false },
            { name: "throughExpiry", type: "uint64", indexed: false },
            { name: "uniquePointsProcessed", type: "uint8", indexed: false },
            { name: "extraWeightRemoved", type: "uint256", indexed: false },
            { name: "verifiedTotalWeightAfter", type: "uint256", indexed: false },
        ],
    },
];
export function parseRewardReceipt(receipt) {
    return parseEventLogs({ abi: rewardEventAbi, logs: receipt.logs, strict: false });
}
//# sourceMappingURL=rewards.js.map